import {
    useDocsI18n
} from "../i18n";
import type {
    DocsTocItem
} from "../components/toc";
import {
    BulletList,
    PageHeader,
    InlineCode,
    CodeBlock,
    Article,
    Callout,
    Section,
    Prose
} from "../components/content";

type GuideNoteBlock = {
    titleKey?: string;
    type: "note";
    key: string;
};

type GuideCodeBlock = {
    language: string;
    type: "code";
    code: string;
};

type GuideListBlock = {
    keys: Array<string>;
    type: "list";
};

type GuidePathsBlock = {
    items: Array<{
        path: string;
        key: string;
    }>;
    type: "paths";
};

type GuideTextBlock = {
    type: "text";
    key: string;
};

type GuideBlock = GuideNoteBlock | GuideCodeBlock | GuideListBlock | GuideTextBlock | GuidePathsBlock;

type GuideSection = {
    blocks: Array<GuideBlock>;
    titleKey: string;
    id: string;
};

type GuideDefinition = {
    sections: Array<GuideSection>;
    groupKey: string;
    titleKey: string;
    leadKey: string;
};

const THEME_SWITCH_CODE = `import {
    NCoreUIKitTheme,
    Text
} from "ncore-ui-kit";

NCoreUIKitTheme.switch({
    themeKey: "system"
});

NCoreUIKitTheme.switch({
    gapPropagation: "compact",
    paletteKey: "caycore",
    sharpness: "rounded"
});

const ThemeStatus = () => {
    const {
        activeThemePreference,
        activeTheme
    } = NCoreUIKitTheme.useContext();

    return <Text>
        {\`\${activeThemePreference} → \${activeTheme}\`}
    </Text>;
};`;

const THEME_PERSISTENCE_CODE = `import {
    NCoreUIKitTheme,
    setupNCoreUIKit
} from "ncore-ui-kit";

const saved = readPreferences();

const NCoreUIKitBase = setupNCoreUIKit({
    initialSelectedGapPropagation: saved?.activeGapPropagation ?? "spacious",
    initialSelectedActiveSharpness: saved?.activeSharpness ?? "soft",
    initialSelectedTheme: saved?.activeThemePreference ?? "system",
    initialSelectedPalette: saved?.activePalette ?? "nibgat"
});

NCoreUIKitTheme.addEventListener("state", (_payload, state) => {
    writePreferences({
        activeThemePreference: state.activeThemePreference,
        activeGapPropagation: state.activeGapPropagation,
        activeSharpness: state.activeSharpness,
        activePalette: state.activePalette
    });
});`;

const CUSTOM_PALETTE_CODE = `import {
    setupNCoreUIKit
} from "ncore-ui-kit";

declare global {
    namespace NCoreUIKit {
        interface PaletteKey extends Record<"acme", unknown> {}
    }
}

const NCoreUIKitBase = setupNCoreUIKit({
    initialSelectedPalette: "acme",
    projectThemes: {
        palettes: [
            {
                name: "acme",
                themes: {
                    light: {
                        system: {
                            default: {
                                primary: "#1d4ed8"
                            },
                            on: {
                                primary: "#ffffff"
                            }
                        }
                    },
                    dark: {
                        system: {
                            default: {
                                primary: "#8ab4ff"
                            },
                            on: {
                                primary: "#0b1f4d"
                            }
                        }
                    }
                }
            }
        ]
    }
});`;

const CUSTOM_THEME_CODE = `<Button
    customTheme={{
        paletteKey: "caycore",
        sharpness: "rounded",
        themeKey: "dark"
    }}
    title="Save"
/>`;

const TOKEN_CODE = `import {
    NCoreUIKitTheme,
    getCornerStyle
} from "ncore-ui-kit";
import {
    View
} from "react-native";

const Panel = () => {
    const {
        shapeConfigs,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={{
            backgroundColor: colors.content.container.subtle,
            padding: spaces.spacingMd,
            ...getCornerStyle({
                radius: radiuses.surface,
                shapeConfigs
            })
        }}
    />;
};`;

const DIRECTION_SOURCE_CODE = `import {
    NCoreUIKitLocalize,
    setupNCoreUIKit
} from "ncore-ui-kit";

const NCoreUIKitBase = setupNCoreUIKit({
    initialSelectedLocale: "tr-TR",
    onDirectionChange: () => {
        restartApp();
    }
});

NCoreUIKitLocalize.switch({
    localeKey: "en-US"
});`;

const DIRECTION_ISLAND_CODE = `<DirectionProvider
    direction="ltr"
>
    <Text>
        +90 555 123 45 67
    </Text>
</DirectionProvider>`;

const DIRECTION_FORMAT_CODE = `import {
    NCoreUIKitLocalize,
    formatCurrency,
    formatDateTime,
    Text
} from "ncore-ui-kit";

const Price = () => {
    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const amount = formatCurrency({
        locale: activeLocale,
        currency: "TRY",
        value: 1234.5
    });

    const date = formatDateTime({
        locale: activeLocale,
        date: new Date(),
        preset: "date"
    });

    return <Text>
        {\`\${amount} · \${date}\`}
    </Text>;
};`;

const DIRECTION_LOCALE_CODE = `import {
    NCoreUIKitLocalize,
    setupNCoreUIKit
} from "ncore-ui-kit";

declare global {
    namespace NCoreUIKit {
        interface LocaleKey extends Record<"ar-SA", unknown> {}
    }
}

const NCoreUIKitBase = setupNCoreUIKit({
    projectLocales: [
        {
            translations: {
                loading: "جارٍ التحميل",
                cancel: "إلغاء"
            },
            locale: "ar-SA",
            isRTL: true
        }
    ],
    onDirectionChange: () => {
        restartApp();
    }
});

NCoreUIKitLocalize.switch({
    localeKey: "ar-SA"
});`;

const getKeys = (prefix: string, count: number): Array<string> => Array.from({
    length: count
}, (_item, index) => `${prefix}${index + 1}`);

const getTextBlock = (key: string): GuideBlock => ({
    type: "text",
    key
});

const getListBlock = (prefix: string, count: number): GuideBlock => ({
    keys: getKeys(prefix, count),
    type: "list"
});

const AI_SKILL_MANUAL_CODE = [
    "mkdir -p .claude/skills .agents/skills .gemini/skills .codex/skills",
    "ln -s ../../node_modules/ncore-ui-kit/skills/ncore-ui-kit .claude/skills/ncore-ui-kit",
    "ln -s ../../node_modules/ncore-ui-kit/skills/ncore-ui-kit .agents/skills/ncore-ui-kit",
    "ln -s ../../node_modules/ncore-ui-kit/skills/ncore-ui-kit .gemini/skills/ncore-ui-kit",
    "ln -s ../../node_modules/ncore-ui-kit/skills/ncore-ui-kit .codex/skills/ncore-ui-kit",
    "ollama create ncore-ui-kit -f node_modules/ncore-ui-kit/skills/ncore-ui-kit/agents/Modelfile"
].join("\n");

const AI_SETUP_CODE = "npx ncore-setup-skill";

const AI_REFERENCE_ITEMS = [
    {
        key: "ai.otherItem1",
        path: "/llms.txt"
    },
    {
        path: "/llms-full.txt",
        key: "ai.otherItem2"
    },
    {
        path: "/llms/components/<name>.md",
        key: "ai.otherItem3"
    }
];

const getCodeBlock = (code: string): GuideBlock => ({
    language: "tsx",
    type: "code",
    code
});

const getNoteBlock = (key: string, titleKey?: string): GuideBlock => ({
    type: "note",
    titleKey,
    key
});

export const GUIDES: Record<string, GuideDefinition> = {
    "/theming": {
        sections: [
            {
                blocks: [
                    getTextBlock("theming.structureText"),
                    getListBlock("theming.structureItem", 3)
                ],
                titleKey: "theming.structureTitle",
                id: "structure"
            },
            {
                blocks: [
                    getTextBlock("theming.palettesText"),
                    getListBlock("theming.palettesItem", 3),
                    getTextBlock("theming.palettesText2"),
                    getTextBlock("theming.palettesText3")
                ],
                titleKey: "theming.palettesTitle",
                id: "palettes"
            },
            {
                blocks: [
                    getTextBlock("theming.modesText"),
                    getTextBlock("theming.modesText2")
                ],
                titleKey: "theming.modesTitle",
                id: "modes"
            },
            {
                blocks: [
                    getTextBlock("theming.densityText"),
                    getListBlock("theming.densityItem", 3),
                    getTextBlock("theming.densityText2"),
                    getListBlock("theming.densityFixed", 4),
                    getNoteBlock("theming.densityNote", "theming.densityNoteTitle")
                ],
                titleKey: "theming.densityTitle",
                id: "density"
            },
            {
                blocks: [
                    getTextBlock("theming.sharpnessText"),
                    getListBlock("theming.sharpnessItem", 5),
                    getTextBlock("theming.sharpnessText2")
                ],
                titleKey: "theming.sharpnessTitle",
                id: "sharpness"
            },
            {
                blocks: [
                    getTextBlock("theming.cornersText"),
                    getListBlock("theming.cornersItem", 4),
                    getTextBlock("theming.cornersText2")
                ],
                titleKey: "theming.cornersTitle",
                id: "corners"
            },
            {
                blocks: [
                    getTextBlock("theming.runtimeText"),
                    getCodeBlock(THEME_SWITCH_CODE),
                    getTextBlock("theming.runtimeText2")
                ],
                titleKey: "theming.runtimeTitle",
                id: "runtime"
            },
            {
                blocks: [
                    getTextBlock("theming.persistenceText"),
                    getCodeBlock(THEME_PERSISTENCE_CODE),
                    getTextBlock("theming.persistenceText2")
                ],
                titleKey: "theming.persistenceTitle",
                id: "persistence"
            },
            {
                blocks: [
                    getTextBlock("theming.customPaletteText"),
                    getCodeBlock(CUSTOM_PALETTE_CODE),
                    getListBlock("theming.customPaletteItem", 3)
                ],
                titleKey: "theming.customPaletteTitle",
                id: "custom-palette"
            },
            {
                blocks: [
                    getTextBlock("theming.customThemeText"),
                    getCodeBlock(CUSTOM_THEME_CODE),
                    getTextBlock("theming.customThemeText2")
                ],
                titleKey: "theming.customThemeTitle",
                id: "custom-theme"
            },
            {
                blocks: [
                    getTextBlock("theming.tokensText"),
                    getListBlock("theming.tokensItem", 3),
                    getTextBlock("theming.tokensText2"),
                    getCodeBlock(TOKEN_CODE)
                ],
                titleKey: "theming.tokensTitle",
                id: "tokens"
            }
        ],
        groupKey: "nav.sectionGettingStarted",
        titleKey: "page.theming",
        leadKey: "theming.lead"
    },
    "/direction": {
        sections: [
            {
                blocks: [
                    getTextBlock("direction.sourceText"),
                    getListBlock("direction.sourceItem", 3),
                    getCodeBlock(DIRECTION_SOURCE_CODE)
                ],
                titleKey: "direction.sourceTitle",
                id: "source"
            },
            {
                blocks: [
                    getTextBlock("direction.islandText"),
                    getCodeBlock(DIRECTION_ISLAND_CODE),
                    getListBlock("direction.islandItem", 3)
                ],
                titleKey: "direction.islandTitle",
                id: "islands"
            },
            {
                blocks: [
                    getTextBlock("direction.mirroringText"),
                    getListBlock("direction.mirroringItem", 5),
                    getTextBlock("direction.mirroringText2"),
                    getListBlock("direction.mirroringFixed", 5)
                ],
                titleKey: "direction.mirroringTitle",
                id: "mirroring"
            },
            {
                blocks: [
                    getTextBlock("direction.logicalText"),
                    getListBlock("direction.logicalItem", 4),
                    getTextBlock("direction.logicalText2")
                ],
                titleKey: "direction.logicalTitle",
                id: "start-end"
            },
            {
                blocks: [
                    getTextBlock("direction.formattingText"),
                    getCodeBlock(DIRECTION_FORMAT_CODE),
                    getListBlock("direction.formattingItem", 5)
                ],
                titleKey: "direction.formattingTitle",
                id: "formatting"
            },
            {
                blocks: [
                    getTextBlock("direction.fontsText"),
                    getListBlock("direction.fontsItem", 6)
                ],
                titleKey: "direction.fontsTitle",
                id: "fonts"
            },
            {
                blocks: [
                    getTextBlock("direction.addLocaleText"),
                    getCodeBlock(DIRECTION_LOCALE_CODE),
                    getListBlock("direction.addLocaleItem", 3)
                ],
                titleKey: "direction.addLocaleTitle",
                id: "add-locale"
            },
            {
                blocks: [
                    getTextBlock("direction.testingText"),
                    getListBlock("direction.testingItem", 6)
                ],
                titleKey: "direction.testingTitle",
                id: "testing"
            }
        ],
        groupKey: "nav.sectionGettingStarted",
        titleKey: "page.direction",
        leadKey: "direction.lead"
    },
    "/ai": {
        sections: [
            {
                blocks: [
                    getTextBlock("ai.setupText"),
                    {
                        code: AI_SETUP_CODE,
                        language: "bash",
                        type: "code"
                    },
                    getTextBlock("ai.setupText2"),
                    {
                        code: AI_SKILL_MANUAL_CODE,
                        language: "bash",
                        type: "code"
                    },
                    getTextBlock("ai.setupText3")
                ],
                titleKey: "ai.setupTitle",
                id: "setup"
            },
            {
                blocks: [
                    getTextBlock("ai.otherText"),
                    {
                        items: AI_REFERENCE_ITEMS,
                        type: "paths"
                    }
                ],
                titleKey: "ai.otherTitle",
                id: "other-agents"
            },
            {
                blocks: [
                    getListBlock("ai.knowsItem", 4)
                ],
                titleKey: "ai.knowsTitle",
                id: "knows"
            },
            {
                blocks: [
                    getTextBlock("ai.accurateText")
                ],
                titleKey: "ai.accurateTitle",
                id: "accurate"
            }
        ],
        groupKey: "nav.sectionGettingStarted",
        titleKey: "page.ai",
        leadKey: "ai.lead"
    }
};

export const getGuideToc = (path: string, t: (key: string) => string): Array<DocsTocItem> => (GUIDES[path]?.sections ?? []).map((section) => ({
    title: t(section.titleKey),
    id: section.id
}));

const GuidePage = ({
    path
}: {
    path: string;
}) => {
    const {
        t
    } = useDocsI18n();

    const guide = GUIDES[path];

    if(!guide) {
        return null;
    }

    const renderBlock = (block: GuideBlock, key: string) => {
        if(block.type === "text") {
            return <Prose
                text={t(block.key)}
                key={key}
            />;
        }

        if(block.type === "list") {
            return <BulletList
                items={block.keys.map((itemKey) => t(itemKey))}
                key={key}
            />;
        }

        if(block.type === "paths") {
            return <BulletList
                items={block.items.map((item) => <>
                    <InlineCode>
                        {item.path}
                    </InlineCode>
                    {` ${t(item.key)}`}
                </>)}
                key={key}
            />;
        }

        if(block.type === "code") {
            return <CodeBlock
                language={block.language}
                code={block.code}
                key={key}
            />;
        }

        return <Callout
            title={block.titleKey ? t(block.titleKey) : undefined}
            key={key}
        >
            {t(block.key)}
        </Callout>;
    };

    return <Article>
        <PageHeader
            breadcrumbs={[
                {
                    label: t("breadcrumb.docs"),
                    href: "/"
                },
                {
                    label: t(guide.groupKey)
                }
            ]}
            title={t(guide.titleKey)}
            lead={t(guide.leadKey)}
        />
        {
            guide.sections.map((section) => <Section
                title={t(section.titleKey)}
                key={section.id}
                id={section.id}
            >
                {
                    section.blocks.map((block, index) => renderBlock(block, `${section.id}-${index}`))
                }
            </Section>)
        }
    </Article>;
};
export default GuidePage;
